<template>
  <div>
    <div class="fish-history-content">
      <div class="fish-history-header">
        <router-link to="/mypage" class="back-link">
          <chevron-left-icon :size="16" fillColor="#67A9E7" />
          {{ t("fishHistory.backToMyPage") }}
        </router-link>
        <div class="fish-history-header-title">{{ t("fishHistory.title") }}</div>
      </div>

      <div class="fish-history-list">
        <div class="history-item" v-for="item in paginatedItemsWithExpiration" :key="item.transactionId">
          <!-- ヘッダー部分 -->
          <div class="history-item-header">
            <div class="header-label">{{ t("fishHistory.total") }}</div>
            <div class="header-date">{{ formatCreatedAt(item.createdAt) }}</div>
          </div>

          <!-- メインコンテンツ -->
          <div class="history-item-main">
            <div class="amount-section">
              <div class="amount-value" :class="{ negative: item.isNegative }">
                {{ formatNumber(item.amount) }} fish
              </div>
            </div>
            <div class="type-section">
              <div class="transaction-type">{{ item.transactionTypeLabel }}</div>
            </div>
          </div>

          <!-- 内訳セクション -->
          <div v-if="shouldShowBreakdown(item)" class="breakdown-section">
            <div class="breakdown-label">{{ t("fishHistory.breakdown") }}</div>
            <div class="breakdown-items">
              <div v-for="(entry, index) in getBreakdownEntries(item)" :key="index" class="breakdown-item">
                <div class="breakdown-source">
                  <span class="source-label">{{ entry.label }}</span>
                  <span class="source-amount">{{ entry.amount.toLocaleString("ja-JP") }}</span>
                  <span v-if="!item.isNegative && shouldShowExpiration(entry.expiredAt)">
                    <span class="expiration-label">{{ t("fishHistory.expirationLabel") }}</span>
                    {{ formatExpiredAt(entry.expiredAt) }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- ローディング状態 -->
        <LoadingSpinner v-if="isLoading" :text="t('fishHistory.loadingText')" size="medium" variant="inline" />

        <!-- エラー状態 -->
        <div v-else-if="error" class="error-state">
          <p class="error-message">{{ error }}</p>
          <button class="retry-button" @click="retryLoadData">{{ t("fishHistory.retryButton") }}</button>
        </div>

        <!-- データなし状態 -->
        <div v-else-if="historyItems.length === 0" class="no-data">
          <p>{{ t("fishHistory.noData") }}</p>
        </div>
      </div>

      <div class="fish-history-footer">
        <div class="pagination">
          <div class="pagination-pages">
            <button
              v-for="page in visiblePages"
              :key="page"
              class="pagination-page"
              :class="{ active: page === currentPage, 'pagination-dots': page === '...' }"
              @click="typeof page === 'number' ? goToPage(page) : null"
              :disabled="page === '...'"
            >
              {{ page }}
            </button>
          </div>
        </div>

        <!-- バッチ読み込みエラー表示 -->
        <div v-if="batchError" class="batch-error-state">
          <p class="batch-error-message">{{ batchError }}</p>
          <LoadingSpinner v-if="isLoadingBatch" :text="t('fishHistory.loadingText')" size="small" variant="inline" />
          <button v-else class="batch-retry-button" @click="retryLoadBatch">{{ t("fishHistory.retryButton") }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from "vue";
import { format } from "date-fns";
import { useI18n } from "vue-i18n-bridge";
import { NolaCoinClient } from "@/lib/clients/nolaCoinClient";
import { FishHistoryItem, FishTransactionType, EntryBreakdown, CoinType } from "@/lib/models/nolaCoin";
import ChevronLeftIcon from "icons/ChevronLeft.vue";
import LoadingSpinner from "@/components/atoms/LoadingSpinner.vue";

// i18nの初期化
const { t } = useI18n();

// APIクライアントのインスタンス
const nolaCoinClient = new NolaCoinClient();

// 履歴データの状態管理
const historyItems = ref<FishHistoryItem[]>([]);
const isLoading = ref(false);
const hasMoreData = ref(true);
const error = ref<string | null>(null);
const batchError = ref<string | null>(null); // バッチ読み込みエラー用

// ページネーション設定
const currentPage = ref(1);
const itemsPerPage = ref(10); // 10件ずつ表示
const defaultLimit = ref(100); // デフォルトlimit

// API関連の状態管理
const currentBatchNumber = ref(1); // 現在のバッチ番号（1から開始）
const maxBatches = ref(5); // 最大バッチ数（合計500件まで）
const isLoadingBatch = ref(false); // バッチ読み込み中フラグ
const nextLastKey = ref<string | undefined>(undefined); // 次のページ用のlastKey

const hasMorePages = computed(() => currentBatchNumber.value < maxBatches.value && hasMoreData.value); // さらにページがあるかどうか

// 計算プロパティ
const totalItems = computed(() => historyItems.value.length);
const currentBatchPages = computed(() => Math.ceil(totalItems.value / itemsPerPage.value));

const startIndex = computed(() => (currentPage.value - 1) * itemsPerPage.value);
const endIndex = computed(() => Math.min(startIndex.value + itemsPerPage.value, totalItems.value));

const paginatedItems = computed(() => historyItems.value.slice(startIndex.value, endIndex.value));

// ページネーション対象のアイテムで有効期限付きのもの
const paginatedItemsWithExpiration = computed(() =>
  paginatedItems.value.map((item) => {
    const expirationDate = getExpirationDate(item);
    return {
      ...item,
      transactionTypeLabel: getTransactionTypeLabel(item.transactionType),
      expirationDateFormatted: expirationDate
        ? format(new Date(expirationDate), t("fishHistory.dateFormat.expiredAt"))
        : null,
      isNegative: item.amount < 0,
    };
  })
);

// 新しいページネーション表示ロジック
const visiblePages = computed(() => {
  const pages: Array<number | string> = [];
  const totalPages = currentBatchPages.value;
  const current = currentPage.value;

  // 基本的には：1 ... currentPage-1 currentPage currentPage+1 ... totalPages
  // ただし、隣接する場合は...を省略

  // データがない場合は空配列を返す
  if (totalPages === 0) {
    return pages;
  }

  // ページが1つだけの場合
  if (totalPages === 1) {
    pages.push(1);
    return pages;
  }

  // 最初のページ（1）を追加
  pages.push(1);

  // 現在のページが1の場合
  if (current === 1) {
    if (totalPages > 1) {
      pages.push(2);
    }
    // 2ページ以上のギャップがある場合のみ省略記号を表示
    if (totalPages > 4) {
      pages.push("...");
    }
    // 3ページ目がある場合は3を表示、ない場合は最後のページを表示
    if (totalPages === 3) {
      pages.push(3);
    } else if (totalPages === 4) {
      pages.push(3);
      pages.push(4);
    } else if (totalPages > 4) {
      pages.push(totalPages);
    }
    return pages;
  }

  // 現在のページが最後のページの場合
  if (current === totalPages) {
    // 2ページ以上のギャップがある場合のみ省略記号を表示
    if (totalPages > 4) {
      pages.push("...");
    }
    // 前のページを適切に表示
    if (totalPages === 2) {
      pages.push(2);
    } else if (totalPages === 3) {
      pages.push(2);
      pages.push(3);
    } else if (totalPages === 4) {
      pages.push(2);
      pages.push(3);
      pages.push(4);
    } else if (totalPages > 4) {
      pages.push(totalPages - 1);
      pages.push(totalPages);
    }
    return pages;
  }

  // 現在のページが中間の場合
  const prevPage = current - 1;
  const nextPage = current + 1;

  // 1とprevPageの間に...が必要かチェック
  if (prevPage > 2) {
    pages.push("...");
  }

  // 前のページを追加（1でない場合）
  if (prevPage > 1) {
    pages.push(prevPage);
  }

  // 現在のページを追加（1でない場合）
  if (current > 1) {
    pages.push(current);
  }

  // 次のページを追加（最後でない場合）
  if (nextPage < totalPages) {
    pages.push(nextPage);
  }

  // nextPageと最後のページの間に...が必要かチェック
  if (nextPage < totalPages - 1) {
    pages.push("...");
  }

  // 最後のページを追加（現在のページと異なる場合）
  if (totalPages > 1 && current !== totalPages) {
    pages.push(totalPages);
  }

  return pages;
});

// メソッド
const goToPage = (page: number) => {
  if (page >= 1 && page <= currentBatchPages.value) {
    currentPage.value = page;
    // ページ変更時にスクロール位置をトップに戻す
    window.scrollTo({ top: 0, behavior: "smooth" });
    // watcherが自動的に次のバッチを読み込むかどうかを判断
  }
};

// 最初のデータを読み込む
const loadInitialData = async () => {
  if (isLoading.value) return;

  isLoading.value = true;
  error.value = null;

  try {
    const response = await nolaCoinClient.listTransactionHistory({
      limit: defaultLimit.value,
    });

    historyItems.value = response.items;
    hasMoreData.value = !!response.lastKey; // lastKeyが存在すれば次のページがある
    nextLastKey.value = response.lastKey;
  } catch (err) {
    console.error("Failed to load initial data:", err);
    error.value = t("fishHistory.errorMessage");
    historyItems.value = []; // エラー時は空配列にして、エラー表示を優先
    hasMoreData.value = false;
  } finally {
    isLoading.value = false;
  }
};

// 再試行関数
const retryLoadData = async () => {
  await loadInitialData();
};

// 新しいバッチを読み込む（実際のAPI版）
const loadNextBatch = async () => {
  if (isLoadingBatch.value || !hasMorePages.value) {
    return;
  }

  isLoadingBatch.value = true;
  batchError.value = null; // エラー状態をクリア

  try {
    const response = await nolaCoinClient.listTransactionHistory({
      limit: defaultLimit.value,
      lastKey: nextLastKey.value,
    });

    // 既存のデータに追加
    historyItems.value.push(...response.items);

    // バッチ番号を更新
    currentBatchNumber.value += 1;

    // 次のページ情報を更新
    hasMoreData.value = !!response.lastKey;
    nextLastKey.value = response.lastKey;
  } catch (err) {
    console.error("Failed to load batch data:", err);
    batchError.value = t("fishHistory.batchErrorMessage");
    // エラー時は追加読み込みを停止
    hasMoreData.value = false;
  } finally {
    isLoadingBatch.value = false;
  }
};

// バッチ読み込み再試行関数
const retryLoadBatch = async () => {
  // エラー状態をリセットして再試行
  hasMoreData.value = true;
  await loadNextBatch();
};

// TransactionTypeを翻訳するヘルパー関数
const getTransactionTypeLabel = (transactionType: FishTransactionType): string => {
  const key = `fishHistory.transactionType.${transactionType}`;
  const translated = t(key);
  // 翻訳が見つからない場合はトランザクションタイプをそのまま返す
  return translated === key ? transactionType : translated;
};

// 有効期限を取得するヘルパー関数
const getExpirationDate = (item: FishHistoryItem): string | null => {
  // entryBreakdownがあれば取引タイプに関係なくチェック
  if (item.entryBreakdown && item.entryBreakdown.length > 0) {
    // entryBreakdownから有効期限を取得
    const entryWithExpiration = item.entryBreakdown.find((entry) => entry.expiredAt);
    if (entryWithExpiration?.expiredAt) {
      return entryWithExpiration.expiredAt;
    }
  }

  return null;
};

// 有効期限が表示すべきものかを判定するヘルパー関数
const shouldShowExpiration = (expiredAt: string | undefined): boolean => {
  if (!expiredAt) return false;

  // 9999999999999（実質無期限）の場合は非表示
  const INFINITE_EXPIRATION = 9999999999999;
  const expiredAtTimestamp = new Date(expiredAt).getTime();

  return expiredAtTimestamp !== INFINITE_EXPIRATION;
};

// 有効期限をフォーマットするヘルパー関数
const formatExpiredAt = (expiredAt: string | undefined): string => {
  if (!expiredAt) return "";
  const dateFormat = t("fishHistory.dateFormat.expiredAt");
  return format(new Date(expiredAt), dateFormat);
};

// 作成日をフォーマットするヘルパー関数
const formatCreatedAt = (createdAt: string): string => {
  const dateFormat = t("fishHistory.dateFormat.createdAt");
  return format(new Date(createdAt), dateFormat);
};

const formatNumber = (value: number) => {
  const formattedValue = Math.abs(value).toLocaleString("ja-JP");
  return value >= 0 ? `+${formattedValue}` : `-${formattedValue}`;
};

// 内訳表示の判定
const shouldShowBreakdown = (item: FishHistoryItem): boolean =>
  !!(item.entryBreakdown && item.entryBreakdown.length > 0);

// 内訳エントリーを整形して取得
const getBreakdownEntries = (item: FishHistoryItem) => {
  if (!item.entryBreakdown) return [];

  return item.entryBreakdown.map((entry) => ({
    label: getBreakdownSourceLabel(entry),
    amount: Math.abs(entry.amount),
    expiredAt: entry.expiredAt,
  }));
};

// 内訳のソースラベルを取得するヘルパー関数
const getBreakdownSourceLabel = (entry: EntryBreakdown): string => {
  // coinTypeから判断（優先）
  if (entry.coinType) {
    switch (entry.coinType) {
      case CoinType.PREPAID_WEB:
        return t("fishHistory.coinType.chargeWeb");
      case CoinType.PREPAID_IOS:
        return t("fishHistory.coinType.chargeIOS");
      case CoinType.PREPAID_ANDROID:
        return t("fishHistory.coinType.chargeAndroid");
      case CoinType.BONUS:
      case CoinType.BASE:
        return t("fishHistory.coinType.bonus");
      default:
        return entry.coinType;
    }
  }
  return "";
};

// 現在のページが最後のページ近くになった時に自動的に次のバッチを読み込む
watch(
  [currentPage, currentBatchPages, hasMorePages],
  async ([newPage, batchPages, morePages]) => {
    // 最後のページにアクセスした時に次のバッチを読み込む
    if (newPage >= batchPages && morePages && !isLoadingBatch.value) {
      await loadNextBatch();
    }
  },
  { immediate: false }
);

// コンポーネントマウント時に初期データを読み込む
onMounted(() => {
  loadInitialData();
});
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

.fish-history-content {
  width: 670px;
  height: 100%;
  margin: 0 auto;
  padding: 20px;
  display: flex;
  flex-direction: column;
  background-color: var(--color-background-normal);
  color: var(--color-text-normal);
}

.fish-history-header {
  display: flex;
  flex-direction: column;
  padding: 20px 0;

  .back-link {
    align-self: flex-start;
    color: var(--color-primary);
    text-decoration: none;
    font-size: var(--font-size-sm);
    margin-bottom: 10px;
    display: flex;
    align-items: center;
    gap: 4px;
    line-height: 1;

    &:hover {
      text-decoration: underline;
    }
  }

  .fish-history-header-title {
    font-size: var(--font-size-lg);
    font-weight: var(--font-weight-bold);
    text-align: left;
    color: var(--color-text-normal);
  }

  .batch-info {
    font-size: var(--font-size-sm);
    color: var(--color-text-low);
    margin-top: 0.5rem;
  }
}

.fish-history-list {
  flex: 1;
  padding: 20px 0;
  min-height: 0;

  .history-item {
    display: flex;
    flex-direction: column;
    padding: 16px;
    margin-bottom: 16px;
    background-color: var(--color-surface-base);
    border-radius: var(--radius-sm);
    gap: 12px;

    .history-item-header {
      display: flex;
      justify-content: space-between;
      align-items: center;

      .header-label {
        font-size: var(--font-size-xs);
        color: var(--color-text-low);
        font-weight: var(--font-weight-regular);
      }

      .header-date {
        font-size: var(--font-size-xs);
        color: var(--color-text-low);
        font-weight: var(--font-weight-regular);
      }
    }

    .history-item-main {
      display: flex;
      justify-content: space-between;
      align-items: center;

      .amount-section {
        .amount-value {
          font-size: var(--font-size-lg);
          font-weight: var(--font-weight-bold);
          color: var(--color-text-normal); // プラス表示

          &.negative {
            color: var(--color-warning); // マイナス表示
          }
        }
      }

      .type-section {
        .transaction-type {
          font-size: var(--font-size-md);
          font-weight: var(--font-weight-bold);
          color: var(--color-text-normal);
        }
      }
    }

    .breakdown-section {
      .breakdown-label {
        font-size: var(--font-size-xs);
        color: var(--color-text-low);
        margin-bottom: 8px;
        font-weight: var(--font-weight-regular);
      }

      .breakdown-items {
        display: flex;
        flex-direction: column;
        gap: 4px;

        .breakdown-item {
          .breakdown-source {
            display: flex;
            align-items: center;
            gap: 8px;

            .source-label {
              font-size: var(--font-size-sm);
              color: var(--color-text-normal);
              min-width: 140px;
            }

            .source-amount {
              font-size: var(--font-size-sm);
              color: var(--color-text-normal);
              font-weight: var(--font-weight-regular);
              min-width: 60px;
            }

            .expiration-label {
              background-color: var(--color-surface-muted);
              color: var(--color-text-low);
              padding: 2px 8px;
              border-radius: var(--radius-md);
              font-size: var(--font-size-xs);
              font-weight: var(--font-weight-regular);
              white-space: nowrap;
              margin-right: 4px;
            }
          }
        }
      }
    }
  }

  .no-data {
    text-align: center;
    padding: 40px 20px;
    color: var(--color-text-low);

    p {
      margin: 0;
      font-size: var(--font-size-md);
    }
  }

  .error-state {
    text-align: center;
    padding: 40px 20px;

    .error-message {
      margin: 0 0 15px 0;
      color: var(--color-warning);
      font-size: var(--font-size-md);
      line-height: 1.4;
    }

    .retry-button {
      position: relative;
      background-color: var(--color-primary);
      color: var(--color-text-on-fixed-bg);
      border: none;
      border-radius: var(--radius-xs);
      padding: 10px 20px;
      font-size: var(--font-size-sm);
      font-weight: var(--font-weight-bold);
      cursor: pointer;

      @include hover-overlay;

      &:disabled {
        background-color: var(--color-surface-muted);
        color: var(--color-inactive);
        border: solid 1px var(--color-border);
        cursor: not-allowed;
        &::after {
          display: none;
        }
      }
    }
  }
}

.fish-history-footer {
  padding: 20px 0;
  display: flex;
  flex-direction: column;
  gap: 15px;

  .pagination {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;

    .pagination-pages {
      display: flex;
      gap: 5px;
      margin: 0;

      .pagination-page {
        position: relative;
        width: 36px;
        height: 36px;
        border: none;
        background-color: var(--color-surface-muted);
        color: var(--color-text-normal);
        border-radius: var(--radius-circle);
        cursor: pointer;
        font-size: var(--font-size-sm);
        font-weight: var(--font-weight-bold);
        display: flex;
        align-items: center;
        justify-content: center;

        @include hover-overlay;

        &.active {
          background-color: var(--color-background-invert);
          color: var(--color-text-invert);

          &::after {
            display: none;
          }
        }

        &.pagination-dots {
          cursor: default;
          color: var(--color-text-low);
          background-color: transparent;

          &::after {
            display: none;
          }
        }

        &:disabled {
          cursor: not-allowed;
          opacity: 0.6;
          &::after {
            display: none;
          }
        }
      }
    }
  }

  .pagination-info {
    text-align: center;
    font-size: var(--font-size-sm);
    color: var(--color-text-low);

    .more-indicator {
      color: var(--color-primary);
      font-size: var(--font-size-xs);
      margin-left: 0.5rem;
    }

    .loading-indicator {
      color: var(--color-primary);
      font-size: var(--font-size-xs);
      margin-left: 0.5rem;
      font-weight: var(--font-weight-bold);
    }
  }

  .batch-error-state {
    text-align: center;
    padding: 15px 0;
    border-top: 1px solid var(--color-border);
    margin-top: 15px;

    .batch-error-message {
      margin: 0 0 10px 0;
      color: var(--color-warning);
      font-size: var(--font-size-sm);
      line-height: 1.4;
    }

    .batch-retry-button {
      position: relative;
      background-color: var(--color-warning);
      color: var(--color-text-on-fixed-bg);
      border: none;
      border-radius: var(--radius-xs);
      padding: 8px 16px;
      font-size: var(--font-size-xs);
      font-weight: var(--font-weight-bold);
      cursor: pointer;

      @include hover-overlay;

      &:disabled {
        background-color: var(--color-surface-muted);
        color: var(--color-inactive);
        border: solid 1px var(--color-border);
        cursor: not-allowed;
        &::after {
          display: none;
        }
      }
    }
  }
}

// レスポンシブ対応
@media (max-width: 768px) {
  .fish-history-content {
    width: 100%;
    padding: 10px;
  }

  .fish-history-header {
    .back-link {
      font-size: var(--font-size-xs);
    }

    .fish-history-header-title {
      font-size: var(--font-size-lg);
    }
  }

  .fish-history-list {
    .history-item {
      padding: 12px;
      gap: 10px;

      .history-item-main {
        flex-direction: column;
        align-items: flex-start;
        gap: 8px;

        .amount-section {
          .amount-value {
            font-size: var(--font-size-lg);

            &.negative {
              color: #dc3545; // 赤色でマイナス表示
            }
          }
        }

        .type-section {
          .transaction-type {
            font-size: var(--font-size-sm);
          }
        }
      }

      .breakdown-section {
        .breakdown-items {
          .breakdown-item {
            .breakdown-source {
              flex-wrap: wrap;
              gap: 4px;

              .source-label {
                min-width: auto;
              }
            }
          }
        }
      }
    }
  }

  .fish-history-footer {
    .pagination {
      flex-wrap: wrap;
      gap: 5px;

      .pagination-pages {
        margin: 0;

        .pagination-page {
          width: 32px;
          height: 32px;
          font-size: var(--font-size-xs);
        }
      }
    }
  }
}
</style>
